


















































































import { Component, Vue } from 'vue-property-decorator';
import dayjs from 'dayjs';
import isBetween from 'dayjs/plugin/isBetween';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import { ApplicantList } from '@/@types/LibraryComponent';
import CommonButton from '@/components/common/CommonButton.vue';
import ApplicantListDialog from '@/components/home/ApplicantListDialog.vue';
import ApplicantListCard from '@/components/recruitment/ApplicantListCard.vue';
import RecruitmentNavigationDrawer from '@/components/recruitment/RecruitmentNavigationDrawer.vue';
import AnalyticsData from '@/components/recruitment/dashboard/AnalyticsData.vue';
import applicants from '@/components/recruitment/dashboard/data/applicants';
import applications from '@/components/recruitment/dashboard/data/applications';
import favorites from '@/components/recruitment/dashboard/data/favorites';
import jobs, { Job } from '@/components/recruitment/dashboard/data/jobs';
import occupations, { Occupation } from '@/components/recruitment/dashboard/data/occupations';

dayjs.extend(isBetween);
dayjs.extend(isSameOrBefore);
dayjs.extend(isSameOrAfter);

const now = dayjs();

@Component({
  components: {
    CommonButton,
    ApplicantListDialog,
    ApplicantListCard,
    RecruitmentNavigationDrawer,
    AnalyticsData,
  },
})

export default class DashboardBase extends Vue {
  private isDrawerClose: boolean = false;
  private timelineButtonColor: string = '#1976d2';
  private applicants: Array<ApplicantList> = applicants;
  private jobs: Array<Job> = jobs;
  private occupations: Array<Occupation> = occupations;
  private periodFromDefault: string = now.subtract(6, 'day').format('YYYY-MM-DD');
  private periodToDefault: string = now.format('YYYY-MM-DD');
  private applicationsTotal: number = 0;
  private applicationsCompared: {
    periodFrom: string;
    periodTo: string;
    total: number;
  } | null = null;
  private applicationsLineChartData: Array<{
    label: string;
    value: number;
  }> = [];
  private applicationsDoughnutChartData: Array<{
    label: string;
    value: number;
    color: string;
  }> = [];
  private favoritesTotal: number = 0;
  private favoritesCompared: {
    periodFrom: string;
    periodTo: string;
    total: number;
  } | null = null;
  private favoritesLineChartData: Array<{
    label: string;
    value: number;
  }> = [];
  private favoritesDoughnutChartData: Array<{
    label: string;
    value: number;
    color: string;
  }> = [];

  private goToHumanResourceSearch() {
    this.$router.push({ name: 'humanResourceSearch' });
  }
  private filterByJob(data: Array<{ jobId: string; }>, jobId: string): any {
    if (!jobId || jobId === '99') {
      return data;
    }
    return data.filter(({ jobId: jId }: { jobId: string }) => jId === jobId);
  }
  private filterByPeriod(data: Array<{ date: string; }>, periodFrom: string, periodTo: string): any {
    return data.filter(({ date }: { date: string }) => {
      if (periodFrom && periodTo) {
        return dayjs(date).isBetween(periodFrom, periodTo, 'date', '[]')
      }
      if (periodFrom) {
        return dayjs(date).isSameOrAfter(periodFrom, 'date')
      }
      if (periodTo) {
        return dayjs(date).isSameOrBefore(periodTo, 'date')
      }
      return true;
    });
  }
  private getTotal(
    data: Array<{
      value: number;
    }>,
  ) {
    return data.reduce(
      (accumulator: number, current: { value: number; }) => accumulator + current.value,
      0,
    );
  }
  private getComparedData(
    data: Array<{
      jobId: string;
      date: string;
    }>,
    jobId: string,
    periodFrom: string,
    periodTo: string,
  ) {
    if (!periodFrom || !periodTo) {
      return null;
    }
    const pf = dayjs(periodFrom);
    const pt = dayjs(periodTo);
    const period = pt.diff(pf, 'date') + 1;
    const periodFromCompared = pf.subtract(period, 'date');
    const periodToCompared = pt.subtract(period, 'date');

    const totalCompared = this.getTotal(
      this.filterByPeriod(
        this.filterByJob(data, jobId),
        periodFromCompared.format('YYYY-MM-DD'),
        periodToCompared.format('YYYY-MM-DD'),
      ),
    );

    return {
      periodFrom: periodFromCompared.format('YYYY/MM/DD'),
      periodTo: periodToCompared.format('YYYY/MM/DD'),
      total: totalCompared,
    };
  }
  private getLineChartData(
    data: Array<{
      date: string;
      value: number;
    }>,
  ) {
    return data.reduce((
      results: Array<{ label: string; value: number; }>,
      { date, value }: { date: string; value: number; },
    ) => {
      const currentDate = dayjs(date).format('YYYY/MM/DD');
      const result: { label: string; value: number; } | undefined = (
        results.find(({ label }: { label: string; }) => label === currentDate)
      );
      if (result) {
        result.value += value;
      } else {
        results.push({
          label: currentDate,
          value,
        });
      }
      return results;
    }, []);
  }
  private getDoughnutChartData(
    data: Array<{
      occupationId: string;
      value: number;
    }>,
  ) {
    return data.reduce((
      results: Array<{ label: string; value: number; color: string; }>,
      { occupationId, value }: { occupationId: string; value: number; }
    ) => {
      const currentOccupation: {
        name: string;
        chartColor: string;
      } = this.occupations.find(({ id }: { id: string }) => id === occupationId) || {
        name: 'その他',
        chartColor: '',
      };
      const result: { label: string; value: number; color: string; } | undefined = (
        results.find(({ label }: { label: string; }) => label === currentOccupation.name)
      );
      if (result) {
        result.value += value;
      } else {
        results.push({
          label: currentOccupation.name,
          value,
          color: currentOccupation.chartColor,
        });
      }
      return results;
    }, []);
  }
  private displayApplicationsAnalyticsData({
    jobId,
    periodFrom,
    periodTo,
  }: {
    jobId: string;
    periodFrom: string;
    periodTo: string;
  }) {
    const filteredApplications = this.filterByPeriod(
      this.filterByJob(applications, jobId),
      periodFrom,
      periodTo,
    );
    this.applicationsTotal = this.getTotal(filteredApplications);
    this.applicationsCompared = this.getComparedData(applications, jobId, periodFrom, periodTo);
    this.applicationsLineChartData = this.getLineChartData(filteredApplications);
    this.applicationsDoughnutChartData = this.getDoughnutChartData(filteredApplications);
  }
  private displayFavoritesAnalyticsData({
    jobId,
    periodFrom,
    periodTo,
  }: {
    jobId: string;
    periodFrom: string;
    periodTo: string;
  }) {
    const filteredFavorites = this.filterByPeriod(
      this.filterByJob(favorites, jobId),
      periodFrom,
      periodTo,
    );
    this.favoritesTotal = this.getTotal(filteredFavorites);
    this.favoritesCompared = this.getComparedData(favorites, jobId, periodFrom, periodTo);
    this.favoritesLineChartData = this.getLineChartData(filteredFavorites);
    this.favoritesDoughnutChartData = this.getDoughnutChartData(filteredFavorites);
  }

  private mounted() {
    // アナリティクスデータを表示
    this.displayApplicationsAnalyticsData({
      jobId: '99',
      periodFrom: this.periodFromDefault,
      periodTo: this.periodToDefault,
    });
    this.displayFavoritesAnalyticsData({
      jobId: '99',
      periodFrom: this.periodFromDefault,
      periodTo: this.periodToDefault,
    });
  }
}
